<script setup lang="ts">
import type { ModernSelectIcon } from "./VModernSelectProps.interface";
import { computed } from "vue";
import VIcon from "../Icon/VIcon.vue";
import { modernSelectIconTileCva, modernSelectOptionCva } from "./VModernSelect.cva";

defineOptions({
  name: "ListItem",
});

const {
  description = null,
  icon = null,
  selected = false,
  active = false,
  disabled = false,
  disabledReason = null,
} = defineProps<Props>();

const emit = defineEmits<{
  select: []
  activate: []
}>();

export interface Props {
  id: string
  index: number
  /** Label as escaped HTML, with the matched search text in `<mark>`. */
  label: string
  description?: string | null
  icon?: ModernSelectIcon | null
  selected?: boolean
  active?: boolean
  disabled?: boolean
  disabledReason?: string | null
}

const state = computed(() => {
  if (disabled) {
    return "disabled";
  }
  if (selected) {
    return active ? "selectedActive" : "selected";
  }
  return active ? "active" : "default";
});

const optionClasses = computed(() => modernSelectOptionCva({ twoLine: Boolean(description), state: state.value }));
const iconTileClasses = computed(() => modernSelectIconTileCva({ size: "item", selected: selected && !disabled }));

function onClick() {
  if (disabled) {
    return;
  }
  emit("select");
}
</script>

<template>
  <!-- mousedown keeps focus in the search input, so the panel and its keyboard handling stay in charge. -->
  <div
    :id="id"
    role="option"
    :aria-selected="selected ? 'true' : 'false'"
    :aria-disabled="disabled ? 'true' : undefined"
    :data-option-index="index"
    :class="optionClasses"
    @mousedown.prevent
    @mousemove="emit('activate')"
    @click.stop="onClick"
  >
    <span v-if="icon" :class="iconTileClasses" aria-hidden="true">
      <VIcon :name="icon" />
    </span>
    <span class="min-w-0 flex-1">
      <slot :is-active="selected" :disabled="disabled">
        <span class="block truncate" v-html="label" />
        <span v-if="description" class="block truncate text-xs font-normal text-gray-600 dark:text-gray-400">
          {{ description }}
        </span>
      </slot>
    </span>
    <span v-if="disabled && disabledReason" class="ml-auto shrink-0 text-xs text-gray-500 dark:text-gray-500">
      {{ disabledReason }}
    </span>
    <VIcon
      v-if="selected"
      name="check"
      class="ml-auto shrink-0 text-xs text-primary-600 dark:text-primary-400"
      aria-hidden="true"
    />
  </div>
</template>
